import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import CsrRegister, { listCount } from '../CsrRegister';
import { CsrInlineError, UnderReviewNotice } from '../CsrStatus';
import { useCsrPagedList } from '../../../hooks/useCsrDashboard';
import { fetchCsrSoilTest, fetchCsrSoilTests } from '../../../services/dashboardApi';
import { DASH, errorMessage, fmtDate, orDash, titleCase } from '../csrFormat';
import { SOIL_PARAMETERS, displayValue, fixFirst, parameterStatus, statusLabel } from '../csrSoilParameters';

const COLUMNS = [
  { key: 'sample_id', label: 'Sample ID', width: '130px', className: 'csr-register__id' },
  { key: 'tested_on', label: 'Date', width: '110px', render: (r) => fmtDate(r.tested_on) },
  { key: 'place', label: 'District · village', width: 'minmax(170px, 1.6fr)', render: (r) => [r.district, r.village].filter(Boolean).join(' · ') || DASH },
  { key: 'crop', label: 'Crop', width: '110px', render: (r) => orDash(titleCase(r.crop)) },
  { key: 'score', label: 'Score', width: '90px', render: () => DASH },
  { key: 'status', label: 'Status', width: '100px', render: () => DASH },
];

const SoilTestsTab = ({ adminId, state, scopeName, onOpenTest }) => {
  const list = useCsrPagedList(fetchCsrSoilTests, adminId, { state });
  return (
    <CsrRegister
      id="csr-tests"
      title={`Soil tests completed · ${listCount(list)}`}
      subtitle={`Every soil test in ${scopeName}${list.query ? `, matching “${list.query}”` : ''}. Click a row to open the full record.`}
      placeholder="Sample ID, village, crop or Soil Didi"
      list={list}
      columns={COLUMNS}
      rowKey={(r) => r.sample_id}
      rowLabel={(r) => `Open soil test report ${r.sample_id}`}
      onRowClick={(r) => onOpenTest(r.sample_id)}
    />
  );
};

// Opened from every tab, so CsrFarmers renders it; it lives here with the soil tests it describes.
const placeOf = (t) => [t.state, t.district && `${t.district} district`, t.village].filter(Boolean).join(' · ') || DASH;

export const SoilTestReportModal = ({ adminId, sampleId, onClose, onOpenFarmer }) => {
  const [state, setState] = useState({ status: 'loading', test: null, error: '' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!sampleId) return undefined;
    let ignore = false;
    setState({ status: 'loading', test: null, error: '' });
    fetchCsrSoilTest(adminId, sampleId)
      .then((data) => { if (!ignore) setState({ status: 'success', test: data?.test || null, error: '' }); })
      .catch((err) => { if (!ignore) setState({ status: 'error', test: null, error: errorMessage(err, 'Could not load this soil test.') }); });
    return () => { ignore = true; };
  }, [adminId, sampleId, attempt]);

  const t = state.test;
  const problems = t?.parameters ? fixFirst(t.parameters) : [];

  return (
    <Modal show={Boolean(sampleId)} onHide={onClose} centered size="lg" dialogClassName="csr-modal" contentClassName="csr-modal__content" aria-labelledby="csr-tpop-h">
      <div className="csr-modal__head">
        <div className="d-flex flex-column" style={{ gap: 2 }}>
          <span className="csr-kpi__label">{`Soil test report · ${t ? fmtDate(t.tested_on || t.sampled_on) : '…'}`}</span>
          <h2 id="csr-tpop-h">{sampleId}</h2>
          {t ? <span className="csr-row__sub">{placeOf(t)}</span> : null}
        </div>
        <button type="button" className="csr-district__close" onClick={onClose} aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      </div>
      <div className="csr-modal__body">
        {state.status === 'loading' ? <div className="csr-skel" style={{ height: 320 }} aria-busy="true" aria-label="Loading soil test" /> : null}
        {state.status === 'error' ? <CsrInlineError message={state.error} onRetry={() => setAttempt((a) => a + 1)} /> : null}
        {state.status === 'success' && t ? (
          <div className="d-flex flex-column" style={{ gap: 14 }}>
            <div className="csr-modal__facts">
              <div className="csr-district__cell"><span>Tested by Soil Didi</span><strong>{t.field_officer_name ? `${t.field_officer_name} (${t.field_officer_id})` : DASH}</strong></div>
              <div className="csr-district__cell"><span>Crop</span><strong>{orDash(titleCase(t.crop))}</strong></div>
              <div className="csr-district__cell"><span>Soil score · first → retest</span><strong>{`${DASH} → ${DASH}`}</strong></div>
              <button type="button" className="csr-district__cell csr-modal__link" onClick={() => onOpenFarmer(t.user_id)}>
                <span>Farmer record</span><strong>{`${t.user_id} →`}</strong>
              </button>
            </div>

            {t.under_review ? <UnderReviewNotice /> : null}
            {problems.length ? (
              <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                {problems.map((pq) => <span key={pq.key} className="csr-chip csr-chip--amber">{`Fix first: ${pq.label} — ${pq.status.toLowerCase()}`}</span>)}
              </div>
            ) : null}
            {t.parameters && !problems.length ? <span className="csr-chip">All rated parameters are in the ideal range</span> : null}
            {t.parameters ? (
              <>
                <div className="csr-params">
                  {SOIL_PARAMETERS.map((param) => {
                    const value = t.parameters?.[param.key];
                    const status = parameterStatus(param, value);
                    return (
                      <div key={param.key} className="csr-param">
                        <span className="csr-param__name">{param.label}</span>
                        <span className="csr-param__value">
                          <strong>{displayValue(value)}</strong>{' → '}<strong>{DASH}</strong>{param.unit ? <span className="csr-row__sub"> {param.unit}</span> : null}
                          {status ? <span className={`csr-param__status is-${status}`}>{statusLabel(param, status)}</span> : null}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="csr-row__sub">First test values shown; retest values (→) appear once retests are recorded.</p>
              </>
            ) : (
              <p className="csr-register__empty"><strong>Not tested</strong> — this sample has been booked, but no soil test report has been recorded for it yet.</p>
            )}
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

export default SoilTestsTab;
